import React, { useMemo } from 'react';

import { Icon } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { isAllowedAppAccess } from '~/lib/session';
import { useSessionStore } from '~/stores/Session';

import { NavItem } from './NavMain';
import {
  CARDS_ENTRIES,
  DOCUMENT_ENTRIES,
  ENTITIES_ENTRIES,
  EVENTS,
  LOANS_ENTRIES,
  PLATFORM_ENTRIES,
  TRANSFER_ENTRIES,
  USER_ENTRIES,
} from './sidebarRoutes';

const toSubItems = (entries: { label: string; path: string; activePath?: string }[]) =>
  entries.map((e) => ({ title: e.label, path: e.path, activePath: e.activePath }));

export const useNavItems = (): NavItem[] => {
  const { currentPlatform, currentUser, loginAuthMethod, mfaStatus } = useSessionStore();

  const featureFlagRequireLiveVerification = useFeatureFlag(FeatureFlag.EnableLiveModeMfaRequired);
  const allowAppAccess = useMemo(
    () => isAllowedAppAccess(currentPlatform, mfaStatus, featureFlagRequireLiveVerification, loginAuthMethod),
    [currentPlatform, currentUser, featureFlagRequireLiveVerification, loginAuthMethod, mfaStatus]
  );

  const customRolesEnabled = useFeatureFlag(FeatureFlag.DashboardCustomRoles);
  const platformEntries = useMemo(() => {
    if (customRolesEnabled) {
      return PLATFORM_ENTRIES;
    }
    return PLATFORM_ENTRIES.filter((entry) => entry.label !== 'Roles');
  }, [customRolesEnabled]);

  const transferBatchesEnabled = useFeatureFlag(FeatureFlag.EnableTransferBatches);
  const transferEntries = useMemo(() => {
    if (transferBatchesEnabled) {
      return TRANSFER_ENTRIES;
    }
    return TRANSFER_ENTRIES.filter((entry) => entry.path !== ROUTE.TRANSFER_BATCHES);
  }, [transferBatchesEnabled]);

  const notificationConfigurationEnabled = useFeatureFlag(FeatureFlag.EnableNotificationConfiguration);

  return useMemo<NavItem[]>(() => {
    const items: NavItem[] = [];

    if (allowAppAccess) {
      items.push({ title: 'Home', path: ROUTE.ROOT, icon: <Icon.Home />, exact: true });
      items.push({
        title: 'Platform',
        path: ROUTE.PLATFORM_SETTINGS,
        activePath: ROUTE.PLATFORM_ROOT,
        icon: <Icon.Layers />,
        items: toSubItems(platformEntries),
      });
    }

    items.push({
      title: 'User',
      path: ROUTE.PROFILE,
      icon: <Icon.User />,
      items: notificationConfigurationEnabled ? toSubItems(USER_ENTRIES) : undefined,
      separatorAfter: true,
    });

    if (allowAppAccess) {
      items.push({
        title: 'Transfers',
        path: ROUTE.TRANSFERS,
        icon: <Icon.AnimationArrows />,
        items: toSubItems(transferEntries),
      });
      items.push({
        title: 'Loans',
        path: ROUTE.LOANS,
        icon: <Icon.HandCoins />,
        items: toSubItems(LOANS_ENTRIES),
      });
      items.push({ title: 'Accounts', path: ROUTE.BANK_ACCOUNTS, icon: <Icon.Wallet /> });
      items.push({
        title: 'Entities',
        path: ROUTE.ENTITIES,
        icon: <Icon.Document />,
        items: toSubItems(ENTITIES_ENTRIES),
        separatorAfter: true,
      });
      items.push({
        title: 'Documents',
        path: ROUTE.STATEMENTS,
        activePath: ROUTE.DOCUMENTS,
        icon: <Icon.DocumentEdit />,
        items: toSubItems(DOCUMENT_ENTRIES),
      });
      items.push({
        title: 'Events',
        path: ROUTE.EVENTS,
        activePath: ROUTE.EVENTS,
        icon: <Icon.Calendar />,
        items: toSubItems(EVENTS),
        separatorAfter: !!currentPlatform?.issuerProcessingDashboardEnabled,
      });

      if (currentPlatform?.issuerProcessingDashboardEnabled) {
        items.push({
          title: 'Cards',
          path: ROUTE.CARDS,
          icon: <Icon.Wallet />,
          items: toSubItems(CARDS_ENTRIES),
        });
      }
    }

    return items;
  }, [
    allowAppAccess,
    platformEntries,
    transferEntries,
    notificationConfigurationEnabled,
    currentPlatform?.issuerProcessingDashboardEnabled,
  ]);
};
